import React, { useState, useEffect } from 'react';
import { useOrderModal } from '../contexts/OrderModalContext';
import { useNavigate } from 'react-router-dom';

const HomeSizzleArea = ({ 
  colorMode = "colorBlack", 
  altColorMode = "colorWhite",
  // Fallback props in case API fails
  fallbackImageUrl = "/images/kinders-bckgrd-d1.webp",
  fallbackHeadingOne = "Welcome to Kinder's!",
  fallbackHeadingTwo = "No One Leaves Hungry.",
  fallbackHeadingThree = "Family-owned. Community-loved. Consistently delicious."
}) => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [specialDescription, setSpecialDescription] = useState("Today's Special"); // Dynamic special
  const navigate = useNavigate();
  const { openModal } = useOrderModal();

  // Dynamic box data that uses the fetched special description
  const getBoxData = () => [
    {
      headline: "Order Pickup",
      description: "Packaged with love. Smells amazing.",
      buttonText: "Let's Do This",
      linkUrl: "modal",
      orderType: "pickup"
    },
    {
      headline: "Order Delivery", 
      description: "Hot off the grill, straight to your door.",
      buttonText: "Send the Smoke",
      linkUrl: "modal",
      orderType: "delivery"
    },
    {
      headline: "Request Catering",
      description: "No one ever forgets a Kinder's spread.",
      buttonText: "Book My BBQ",
      linkUrl: "/catering",
      orderType: "pickup-catering"
    },
    {
      headline: "Today's Specials",
      description: specialDescription, // Now using dynamic description
      buttonText: "What's Cooking",
      linkUrl: "/daily-specials",
      orderType: "specials"
    }
  ];

  // Fetch today's special description
  useEffect(() => {
    const fetchSpecialDescription = async () => {
      try {
        const response = await fetch('/api/specials/homepage-special');
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data && data.data.description) {
            setSpecialDescription(data.data.description);
          }
        }
      } catch (err) {
        console.error('Failed to fetch special description:', err);
        // Keep default fallback
      }
    };

    fetchSpecialDescription();
  }, []);

  // Fetch personalized content
  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        
        // Gather device info for better personalization
        const deviceInfo = {
          screenWidth: window.screen.width,
          screenHeight: window.screen.height,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };

        const queryParams = new URLSearchParams({
          ...deviceInfo
        });

        const fetchUrl = `/api/homepage/content?${queryParams}`;
        const response = await fetch(fetchUrl);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success && data.content) {
          setContent(data.content);
        } else if (data.fallback) {
          // Use API fallback
          setContent(data.fallback);
        } else {
          throw new Error(data.error || 'Failed to load content');
        }
      } catch (err) {
        console.error('Failed to fetch homepage content:', err);
        
        // Use component fallback
        setContent({
          contentId: 'component-fallback',
          imageUrl: fallbackImageUrl,
          mobileImageUrl: fallbackImageUrl,
          headingOne: fallbackHeadingOne,
          headingTwo: fallbackHeadingTwo,
          headingThree: fallbackHeadingThree
        });
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount - fallback props don't need to trigger re-fetch

  // Handle order button clicks
  const handleButtonClick = (box) => {
    if (box.linkUrl === "modal") {
      openModal(null, box.orderType); 
    } else {
      navigate(box.linkUrl);
    }
  };

  // Function to get responsive classes for each box
  const getColumnClasses = (index) => {
    // All boxes should be:
    // - Half width on mobile (2x2 grid)
    // - Half width on tablet (2x2 grid)  
    // - One-quarter width on desktop (1x4 grid)
    return "is-flex is-justify-content-center m-0 p-0 column is-6-mobile is-6-tablet is-3-desktop";
  };

  // Show loading state
  if (loading) {
    return (
      <div className="home-sizzle-area">
        <div 
          className="sizzle-background"
          style={{
            backgroundColor: '#2E2E2E',
            width: '100vw',
            height: '120vh',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: -1,
          }}
        />
        <div 
          className="sizzle-content-wrapper"
          style={{
            minHeight: '120vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            position: 'relative',
            zIndex: 1
          }}
        >
          <div className="has-text-centered">
            <span className="mt-3 colorBlack">...</span>
          </div>
        </div>
      </div>
    );
  }

  // Get the appropriate image URL based on device
  const getImageUrl = () => {
    if (!content) {
      
      return fallbackImageUrl;
    }
    
    const isMobile = window.innerWidth <= 768;
    let selectedUrl;
    
    if (isMobile && content.mobileImageUrl) {
      selectedUrl = content.mobileImageUrl;
    } else {
      selectedUrl = content.imageUrl || fallbackImageUrl;
    }
    
    // STRIP QUOTES HERE:
    return selectedUrl.replace(/^"|"$/g, '');
  };

  // Get the current box data with dynamic special description
  const boxData = getBoxData();

  return (
    <div className="home-sizzle-area">
      {/* Background Image Container */}
      <div 
        className="sizzle-background"
        style={{
          backgroundImage: `url(${getImageUrl()})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          width: '100vw',
          height: '120vh',
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: -1,
        }}
      />
      
      {/* Content Container */}
      <div 
        className="sizzle-content-wrapper is-flex is-flex-direction-column is-align-items-center"
      >
        {/* Headlines Content */}
        <div className="pt-0 mt-0 mb-6 mx-6 content-space-160-mobile sizzle-content is-flex is-flex-direction-column">
          {/* Heading One (Salutation) */}
          {content?.headingOne && (
            <div className="m-0 pt-0 pb-4">
              <h1 className="salutation colorWhite">{content.headingOne}</h1>
            </div>
          )}

          {/* Heading Two (Main Headline) */}
          {content?.headingTwo && (
            <div className="m-0 pb-4">
              <h2 className="title colorWhite">{content.headingTwo}</h2>
            </div>
          )}

          {/* Heading Three (Tagline) */}
          {content?.headingThree && (
            <div className="">
              <h3 className="subtitle colorWhite">{content.headingThree}</h3>
            </div>
          )}
        </div>

        {/* Feature Boxes */}
        <div className="home-feature-boxes-container">
          <div className="m-0 p-0 is-flex is-justify-content-center feature-box-grid-wrapper">
            <div className="columns is-flex is-justify-content-center is-mobile is-multiline is-centered feature-boxes-wrapper">
              {boxData.map((box, index) => (
                <div 
                  key={index}
                  className={getColumnClasses(index)}
                >
                  <div 
                    className="mt-5 feature-box"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 12px 32px rgba(0, 0, 0, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.15)';
                    }}
                  >
                    <div className="mt-3 p-0 is-flex is-flex-direction-column is-justify-content-end">
                      {/* Headline */}
                      <div className="m-0 p-0 feature-headline">
                        {box.headline}
                      </div>

                      {/* Description */}
                      <div className="m-0 pt-1 pb-3 feature-description">
                        {box.description}
                      </div>
                    </div>

                    {/* Button */}
                    <div
                      className='button m-0 p-0 is-align-center'
                      role="button"
                      tabIndex="0"
                      onClick={() => handleButtonClick(box)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleButtonClick(box);
                        }
                      }}
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '0.5rem',
                        height: '2.75rem!important',
                        minWidth: '42px',
                        backgroundColor: 'var(--red-color)',
                        color: 'var(--white-color)',
                        borderRadius: '0.25rem',
                        border: '3px solid var(--red-color)',
                        fontSize: '0.874rem',
                        fontStyle: 'normal',
                        fontWeight: '700',
                        lineHeight: '133%',
                        margin: '0',
                        padding: '0',
                        boxShadow: 'none',
                        cursor: 'pointer',
                        textDecoration: 'none',
                        transition: 'all 0.2s ease-in-out',
                        userSelect: 'none'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--green-color)';
                        e.currentTarget.style.borderColor = 'var(--green-color)';
                        e.currentTarget.style.color = 'var(--black-color)';
                        e.currentTarget.style.transform = 'scale(1.02)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--red-color)';
                        e.currentTarget.style.borderColor = 'var(--red-color)';
                        e.currentTarget.style.color = 'var(--white-color)';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      {box.buttonText}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeSizzleArea;